import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3,
  TrendingUp, 
  Users,
  Search,
  Server, 
  Activity,
  Clock,
  Zap,
  Eye,
  FileText,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Calendar,
  Target,
  Database,
  Globe,
  ArrowUpRight,
  ArrowDownRight,
  Percent
} from 'lucide-react';
import { BACKEND_CONFIG } from '../config/backend';

const Dashboard = ({ user, apiStatus = {}, onRefreshApiStatus }) => {
  const [stats, setStats] = useState({
    totalConsultas: 0,
    consultasHoje: 0,
    apiOnline: 0,
    tempoMedioResposta: 0,
    consultasRecentes: []
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false); // Para updates discretos

  // Carregar dados reais do backend
  useEffect(() => {
    const loadDashboardData = async (isBackgroundUpdate = false) => {
      if (isBackgroundUpdate) {
        setIsUpdating(true);
          } else {
        setIsLoading(true);
      }
      
      try {
        console.log('📊 Dashboard: Carregando dados reais...');
        
        // Buscar dados reais do backend
        const response = await fetch(`${BACKEND_CONFIG.BASE_URL}/api/dashboard/stats`, {
            method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
          });
          
          if (response.ok) {
          const data = await response.json();
          console.log('✅ Dashboard: Dados reais carregados:', data);
          
          // Combinar dados reais com informações das APIs
          const apiArray = Object.values(apiStatus);
          const onlineApis = apiArray.filter(api => api?.status === 'online').length;
          const avgApiResponseTime = apiArray
            .filter(api => api?.responseTime)
            .reduce((acc, api) => acc + (api.responseTime || 0), 0) / Math.max(onlineApis, 1);

          setStats({
            totalConsultas: data.totalConsultas || 0,
            consultasHoje: data.consultasHoje || 0,
            apiOnline: onlineApis,
            tempoMedioResposta: data.tempoMedioResposta || Math.round(avgApiResponseTime) || 0,
            consultasRecentes: data.consultasRecentes || { sucesso: [], erro: [], processando: [] },
            totalSucesso: data.totalSucesso || 0,
            totalErro: data.totalErro || 0,
            totalProcessando: data.totalProcessando || 0,
            ultimaAtualizacao: data.ultimaAtualizacao,
            
            // Dados extras reais
            totalCobrancas: data.totalCobrancas || 0,
            cobrancasHoje: data.cobrancasHoje || 0,
            valorTotal: data.valorTotal || 0,
            pagadoresUnicos: data.pagadoresUnicos || 0,
            totalParcelasProcessadas: data.totalParcelasProcessadas || 0,
            totalComissoesProcessadas: data.totalComissoesProcessadas || 0,
            fonte: data.fonte || 'real'
          });
          
              } else {
          console.warn('⚠️ Dashboard: Falha ao carregar dados, usando fallback');
          // Fallback para dados simulados em caso de erro
          const apiArray = Object.values(apiStatus);
          const onlineApis = apiArray.filter(api => api?.status === 'online').length;
          
          setStats({
            totalConsultas: 0,
            consultasHoje: 0,
            apiOnline: onlineApis,
            tempoMedioResposta: 0,
            consultasRecentes: generateRecentConsultas(),
            totalSucesso: 0,
            totalErro: 0,
            totalProcessando: 0
          });
        }
        
            } catch (error) {
        console.error('❌ Dashboard: Erro ao carregar dados:', error);
        
        // Fallback para dados simulados em caso de erro
        const apiArray = Object.values(apiStatus);
        const onlineApis = apiArray.filter(api => api?.status === 'online').length;
        
        setStats({
          totalConsultas: 0,
          consultasHoje: 0,
          apiOnline: onlineApis,
          tempoMedioResposta: 0,
          consultasRecentes: generateRecentConsultas(),
          totalSucesso: 0,
          totalErro: 0,
          totalProcessando: 0,
          erro: 'Dados não disponíveis'
        });
      } finally {
        setIsLoading(false);
        setIsUpdating(false);
      }
    };

    // Primeira carga
    loadDashboardData();
    
    // Updates em background a cada 2 minutos
    const interval = setInterval(() => {
      loadDashboardData(true); // Background update
    }, 120000);

    return () => clearInterval(interval);
  }, [apiStatus]);

  const generateRecentConsultas = () => {
    const tipos = ['PC', 'CPF', 'CNPJ'];
    
    // Gerar consultas com sucesso
    const sucesso = [];
    for (let i = 0; i < 4; i++) {
      const tipo = tipos[Math.floor(Math.random() * tipos.length)];
      sucesso.push({
        id: `sucesso-${i}`,
        tipo,
        codigo: tipo === 'PC' ? `PC-${Math.floor(Math.random() * 100000)}` : 
                `${Math.floor(Math.random() * 99999999999).toString().padStart(11, '0')}`,
        status: 'Sucesso',
        tempo: `${Math.floor(Math.random() * 30) + 1} min atrás`,
        valor: `R$ ${(Math.random() * 5000 + 500).toFixed(2)}`,
        tempoResposta: `${Math.floor(Math.random() * 500) + 200}ms`
      });
    }

    // Gerar consultas com erro
    const erro = [];
    for (let i = 0; i < 3; i++) {
      const tipo = tipos[Math.floor(Math.random() * tipos.length)];
      erro.push({
        id: `erro-${i}`,
        tipo,
        codigo: tipo === 'PC' ? `PC-${Math.floor(Math.random() * 100000)}` : 
                `${Math.floor(Math.random() * 99999999999).toString().padStart(11, '0')}`,
        status: 'Erro',
        tempo: `${Math.floor(Math.random() * 120) + 30} min atrás`,
        erro: ['API indisponível', 'Timeout', 'Documento não encontrado', 'Erro de conexão'][Math.floor(Math.random() * 4)],
        tentativas: Math.floor(Math.random() * 3) + 1
      });
    }

    // Gerar consultas processando
    const processando = [];
    for (let i = 0; i < 2; i++) {
      const tipo = tipos[Math.floor(Math.random() * tipos.length)];
      processando.push({
        id: `processando-${i}`,
        tipo,
        codigo: tipo === 'PC' ? `PC-${Math.floor(Math.random() * 100000)}` : 
                `${Math.floor(Math.random() * 99999999999).toString().padStart(11, '0')}`,
        status: 'Processando',
        tempo: `${Math.floor(Math.random() * 5) + 1} min atrás`,
        progresso: Math.floor(Math.random() * 80) + 20,
        etapa: ['Validando documento', 'Consultando APIs', 'Processando dados', 'Finalizando'][Math.floor(Math.random() * 4)]
      });
    }

    return { sucesso, erro, processando };
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color, trend, trendValue }) => (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5, scale: 1.02 }}
      className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 group cursor-pointer overflow-hidden relative"
    >
      <div className={`absolute top-0 right-0 w-24 h-24 ${color} opacity-10 rounded-full -mr-8 -mt-8 group-hover:scale-110 transition-transform duration-500`}></div>
      
      <div className="relative">
              <div className="flex items-center justify-between mb-4">
          <div className={`p-3 ${color} rounded-xl shadow-lg group-hover:shadow-xl transition-shadow duration-300`}>
            <Icon className="w-6 h-6 text-white" />
                </div>
          {trend && (
            <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
              trend === 'up' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              <span>{trendValue}</span>
                </div>
          )}
              </div>
        
        <div className="mb-2">
          <div className="text-3xl font-bold text-gray-900 mb-1">
            {isLoading ? (
              <div className="animate-pulse bg-gray-200 h-8 w-20 rounded"></div>
            ) : (
              value
            )}
                </div>
          <h3 className="text-sm font-medium text-gray-600">{title}</h3>
                </div>
        
        {subtitle && (
          <p className="text-xs text-gray-500">{subtitle}</p>
        )}
            </div>
          </motion.div>
  );

  const QuickAction = ({ title, description, icon: Icon, color, onClick }) => (
    <motion.button
      onClick={onClick}
      whileHover={{ y: -5, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 text-left group w-full hover:shadow-xl transition-all duration-300"
    >
      <div className={`p-4 ${color} rounded-xl w-fit mb-4 group-hover:scale-110 transition-transform duration-300`}>
        <Icon className="w-6 h-6 text-white" />
                </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </motion.button>
  );

  const ConsultasSection = ({ title, consultas, icon: Icon, color, bgColor, textColor }) => (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
        >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
          <div className={`p-2 ${color} rounded-xl`}>
            <Icon className="w-5 h-5 text-white" />
                </div>
                <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500">{consultas.length} consultas</p>
                </div>
              </div>
        <div className={`px-3 py-1 ${bgColor} ${textColor} rounded-full text-sm font-medium`}>
          {consultas.length}
        </div>
            </div>
            
      <div className="space-y-3">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="flex items-center space-x-3 p-3 bg-gray-100 rounded-lg">
                <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))
        ) : consultas.length > 0 ? (
          consultas.map((consulta, index) => (
                  <motion.div
              key={consulta.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`p-4 ${bgColor} rounded-lg border-l-4 ${color.replace('bg-gradient-to-br', 'border').replace('to-', '').split(' ')[1]} hover:shadow-md transition-all cursor-pointer`}
            >
              <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                  <span className="text-sm font-bold text-gray-900">{consulta.tipo}</span>
                  <span className="text-xs bg-white/50 text-gray-700 px-2 py-1 rounded-full font-mono">
                    {consulta.codigo.length > 12 ? `${consulta.codigo.substring(0, 12)}...` : consulta.codigo}
                  </span>
                          </div>
                <span className="text-xs text-gray-500 font-medium">{consulta.tempo}</span>
                          </div>
                    
              {/* Informações específicas por status */}
              {consulta.status === 'Sucesso' && (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Valor encontrado:</span>
                    <span className="font-semibold text-green-700">{consulta.valor}</span>
                        </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Tempo de resposta:</span>
                    <span className="font-semibold text-green-700">{consulta.tempoResposta}</span>
                      </div>
                        </div>
                    )}
                    
              {consulta.status === 'Erro' && (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Erro:</span>
                    <span className="font-semibold text-red-700">{consulta.erro}</span>
                              </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Tentativas:</span>
                    <span className="font-semibold text-red-700">{consulta.tentativas}x</span>
                        </div>
                      </div>
                    )}
                    
              {consulta.status === 'Processando' && (
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Etapa atual:</span>
                    <span className="font-semibold text-yellow-700">{consulta.etapa}</span>
                              </div>
                  <div className="w-full bg-white/50 rounded-full h-2">
                    <div 
                      className="bg-yellow-500 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${consulta.progresso}%` }}
                    ></div>
                              </div>
                  <div className="text-right">
                    <span className="text-xs font-semibold text-yellow-700">{consulta.progresso}%</span>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))
              ) : (
          <div className="text-center py-6 text-gray-500">
            <Icon className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Nenhuma consulta {title.toLowerCase()}</p>
                      </div>
                    )}
          </div>
        </motion.div>
  );

  // Componente especial para TOP 3 NOVOS
  const Top3NovosSection = ({ data }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl shadow-lg p-6 border border-green-200"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
            <CheckCircle className="h-6 w-6 text-white" />
                          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800">🏆 Top 3 Registros Novos</h3>
            <p className="text-sm text-gray-600">Últimos códigos adicionados ao sistema</p>
                          </div>
                          </div>
        <span className="text-sm text-green-600 font-medium bg-green-100 px-3 py-1 rounded-full">
          🆕 {data?.length || 0} novos
        </span>
                          </div>
      
      <div className="space-y-4">
        {data && data.length > 0 ? (
          data.map((novo, index) => (
            <motion.div
              key={novo.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.2 }}
              className="bg-white rounded-lg border-l-4 border-green-500 p-4 hover:shadow-md transition-all cursor-pointer"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-green-500 text-white rounded-full text-sm font-bold">
                    #{novo.posicao}
                          </div>
                  <div>
                    <span className="text-sm font-bold text-gray-900">{novo.tipo}</span>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-mono ml-2">
                      {novo.codigo.length > 15 ? `${novo.codigo.substring(0, 15)}...` : novo.codigo}
                    </span>
                          </div>
                          </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500">{novo.tempoDecorrido}</div>
                  <div className="text-xs text-green-600 font-medium">{novo.dataSimples}</div>
                          </div>
                        </div>
                    
              <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                  <span className="text-gray-500">Data completa:</span>
                  <div className="font-semibold text-gray-700">{novo.dataCompleta}</div>
                        </div>
                        <div>
                  <span className="text-gray-500">Comissões:</span>
                  <div className="font-semibold text-green-700">{novo.valor}</div>
                        </div>
                        <div>
                  <span className="text-gray-500">Tempo de resposta:</span>
                  <div className="font-semibold text-blue-700">{novo.tempoResposta}</div>
                        </div>
                            <div>
                  <span className="text-gray-500">Endpoint:</span>
                  <div className="font-semibold text-purple-700">{novo.endpoint}</div>
                            </div>
                    </div>
                  </motion.div>
                ))
              ) : (
          <div className="text-center text-gray-500 py-8">
            <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>Nenhum registro novo encontrado</p>
                </div>
              )}
          </div>
        </motion.div>
  );

  return (
    <div className="space-y-8 relative">
      {/* Indicador discreto de atualização */}
      {isUpdating && (
        <div className="fixed top-4 right-4 z-50 bg-blue-500 text-white px-3 py-2 rounded-lg shadow-lg flex items-center space-x-2 animate-pulse">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span className="text-sm">Atualizando...</span>
        </div>
      )}
      {/* Header com boas-vindas */}
          <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-2xl p-8 text-white relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute -top-4 -right-4 w-24 h-24 bg-white/10 rounded-full"></div>
        <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-white/5 rounded-full"></div>
        
        <div className="relative">
          <div className="flex items-center justify-between">
                <div>
              <h1 className="text-3xl font-bold mb-2">
                Olá, {user?.full_name?.split(' ')[0] || 'Usuário'}! 👋
              </h1>
              <p className="text-blue-100 text-lg">
                Bem-vindo ao painel de controle do sistema Bitrix
              </p>
              
              {/* Info discreta da última atualização */}
              <div className="mt-2 text-blue-200 text-xs">
                Última atualização: {stats.ultimaAtualizacao ? 
                  new Date(stats.ultimaAtualizacao).toLocaleString('pt-BR') : 
                  'Carregando...'
                } • Atualiza a cada 2 minutos
                </div>
              <div className="mt-4 flex items-center space-x-4 text-sm">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date().toLocaleDateString('pt-BR', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</span>
              </div>
                <div className="flex items-center space-x-2">
                  <Target className="w-4 h-4" />
                  <span>Sistema funcionando perfeitamente</span>
                    </div>
                  </div>
              </div>
            <div className="hidden lg:block">
              <div className="w-32 h-32 bg-white/20 rounded-2xl flex items-center justify-center">
                <BarChart3 className="w-16 h-16 text-white/80" />
              </div>
              </div>
              </div>
            </div>
          </motion.div>

      {/* Cards de estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total de Consultas"
          value={stats.totalConsultas.toLocaleString()}
          subtitle="Consultas realizadas no sistema"
          icon={Search}
          color="bg-gradient-to-br from-blue-500 to-blue-600"
          trend="up"
          trendValue="12%"
        />
        
        <StatCard
          title="Consultas Hoje"
          value={stats.consultasHoje.toLocaleString()}
          subtitle="Consultas realizadas hoje"
          icon={TrendingUp}
          color="bg-gradient-to-br from-green-500 to-green-600"
          trend="up"
          trendValue="8%"
        />
        
        <StatCard
          title="APIs Online"
          value={`${stats.apiOnline}/${Object.keys(apiStatus).length || 5}`}
          subtitle="APIs funcionando"
          icon={Server}
          color="bg-gradient-to-br from-purple-500 to-purple-600"
          trend={stats.apiOnline >= 3 ? "up" : "down"}
          trendValue="100%"
        />
        
        <StatCard
          title="Tempo Médio"
          value={`${stats.tempoMedioResposta}ms`}
          subtitle="Tempo de resposta das APIs"
          icon={Zap}
          color="bg-gradient-to-br from-orange-500 to-orange-600"
          trend="down"
          trendValue="15%"
        />
                </div>

      {/* Top 3 Registros Novos */}
      <Top3NovosSection data={stats.consultasRecentes?.top3Novos || []} />

      {/* Seções de Consultas por Status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Consultas com Sucesso */}
        <ConsultasSection
          title="Últimas com Sucesso"
          consultas={stats.consultasRecentes.sucesso || []}
          icon={CheckCircle}
          color="bg-gradient-to-br from-green-500 to-emerald-600"
          bgColor="bg-green-50"
          textColor="text-green-700"
        />

        {/* Consultas com Erro */}
        <ConsultasSection
          title="Últimas com Erro"
          consultas={stats.consultasRecentes.erro || []}
          icon={AlertTriangle}
          color="bg-gradient-to-br from-red-500 to-rose-600"
          bgColor="bg-red-50"
          textColor="text-red-700"
        />

        {/* Consultas Processando */}
        <ConsultasSection
          title="Processando Agora"
          consultas={stats.consultasRecentes.processando || []}
          icon={Clock}
          color="bg-gradient-to-br from-yellow-500 to-amber-600"
          bgColor="bg-yellow-50"
          textColor="text-yellow-700"
        />
              </div>
              
      {/* Seção inferior com ações rápidas e status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Ações rápidas - ocupa 2 colunas */}
        <div className="lg:col-span-2 space-y-6">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl">
                <Zap className="w-5 h-5 text-white" />
                          </div>
                          <div>
                <h3 className="text-lg font-semibold text-gray-900">Ações Rápidas</h3>
                <p className="text-sm text-gray-500">Acesso direto às funcionalidades principais</p>
                          </div>
              <button
                onClick={onRefreshApiStatus}
                className="ml-auto p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
                        </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <QuickAction
                title="Nova Consulta"
                description="Pesquisar PC, CPF ou CNPJ"
                icon={Search}
                color="bg-gradient-to-br from-blue-500 to-blue-600"
                onClick={() => window.location.href = '/search'}
              />
              
              <QuickAction
                title="Status das APIs"
                description="Verificar funcionamento"
                icon={Activity}
                color="bg-gradient-to-br from-green-500 to-green-600"
                onClick={() => window.location.href = '/apis'}
              />
              
              <QuickAction
                title="Gerenciar Usuários"
                description="Administrar usuários"
                icon={Users}
                color="bg-gradient-to-br from-purple-500 to-purple-600"
                onClick={() => window.location.href = '/users'}
              />
            </div>
          </motion.div>
        </div>

        {/* Status resumido */}
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
        >
            <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl">
              <Database className="w-5 h-5 text-white" />
              </div>
              <div>
              <h3 className="text-lg font-semibold text-gray-900">Status do Sistema</h3>
              <p className="text-sm text-gray-500">Monitoramento em tempo real</p>
              </div>
            </div>
            
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-gray-900">Sistema Online</span>
                  </div>
              <CheckCircle className="w-5 h-5 text-green-500" />
                  </div>
                  
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-sm font-medium text-gray-900">Backup Atualizado</span>
                  </div>
              <CheckCircle className="w-5 h-5 text-blue-500" />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                <span className="text-sm font-medium text-gray-900">Segurança OK</span>
              </div>
              <CheckCircle className="w-5 h-5 text-purple-500" />
            </div>

            {/* Estatísticas rápidas */}
            <div className="pt-4 border-t border-gray-200">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="p-3 bg-indigo-50 rounded-lg">
                  <div className="text-2xl font-bold text-indigo-600">{stats.consultasHoje}</div>
                  <div className="text-xs text-indigo-500">Hoje</div>
              </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">{stats.apiOnline}</div>
                  <div className="text-xs text-purple-500">APIs OK</div>
            </div>
              </div>
            </div>
            </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;

